import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { hasPendingSubscription, isSubscriber, type SubscriptionFields } from '@langmail/auth-config/access'

import { Button } from '@/components/ui/button'
import { Logo } from '@/components/ui/logo'
import { authClient } from '@/lib/auth-client'

const infoItems = [
  {
    text: (
      <>
        Emails that need you are labeled with{' '}
        <span className="inline-block rounded bg-warning px-2 py-0.5 font-semibold text-warning-foreground text-xs">
          To Do
        </span>
        .
      </>
    ),
  },
  {
    text: 'Notifications, newsletters, promotions, and order updates are labeled and summarized. Nothing is moved or deleted.',
  },
  {
    text: "Every morning at 7 am, you'll get a summary of everything your agent handled.",
  },
]

// Stripe redirects here after checkout with `?checkout=<session id>`. The
// webhook (auth service) is what stores the subscription on the user; the
// page just polls the session until it sees one, then stops trying after 30
// seconds (the receipt email and the Accounts tab are both fine without it).
// Status alone is not enough: early-access accounts are `active` before they
// pay.
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 30_000

type SessionUser = SubscriptionFields | null

async function readSessionUser(): Promise<SessionUser> {
  const result = await authClient.getSession({ query: { disableCookieCache: true } })
  return result.data?.user ?? null
}

type CheckoutState = 'confirming' | 'subscribed' | 'pending' | 'timed_out'

function useCheckoutConfirmation(
  enabled: boolean,
  pollSession: () => Promise<SessionUser>,
  pollInterval: number
): CheckoutState {
  const [state, setState] = useState<CheckoutState>('confirming')

  useEffect(() => {
    if (!enabled) {
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // Absolute deadline: ends the wait even if a poll hangs or keeps failing.
    const deadline = setTimeout(() => {
      if (!cancelled) {
        setState((current) => (current === 'confirming' ? 'timed_out' : current))
      }
    }, POLL_TIMEOUT_MS)
    const tick = async () => {
      let user: SessionUser = null
      try {
        user = await pollSession()
      } catch {
        // A failed read is a miss (auth deploys 502 briefly); keep polling.
      }
      if (cancelled) {
        return
      }
      if (user && isSubscriber(user)) {
        clearTimeout(deadline)
        setState('subscribed')
        return
      }
      // Pending is shown but not final: a card payment can sit `incomplete`
      // for a moment, so keep polling until the subscription is confirmed.
      if (user && hasPendingSubscription(user)) {
        clearTimeout(deadline)
        setState('pending')
      }
      timer = setTimeout(() => {
        tick().catch(() => undefined)
      }, pollInterval)
    }
    tick().catch(() => undefined)
    return () => {
      cancelled = true
      clearTimeout(timer)
      clearTimeout(deadline)
    }
  }, [enabled, pollSession, pollInterval])

  return state
}

const CHECKOUT_COPY: Record<CheckoutState, { heading: string; body: string }> = {
  confirming: { heading: 'Confirming your subscription…', body: 'This usually takes a few seconds.' },
  subscribed: { heading: "You're subscribed.", body: 'Thank you for staying. Your receipt is on its way.' },
  pending: {
    heading: 'Your payment is on its way.',
    body: 'Some payment methods take a few days to clear. Nothing else to do: your plan starts as soon as it does.',
  },
  timed_out: {
    heading: 'Your receipt is on its way.',
    body: 'Your account is ready; your plan will show up under Account shortly.',
  },
}

export const Success = ({
  pollSession = readSessionUser,
  pollInterval = POLL_INTERVAL_MS,
}: {
  pollSession?: () => Promise<SessionUser>
  pollInterval?: number
}) => {
  const [params] = useSearchParams()
  const fromCheckout = params.has('checkout')
  const plan = params.get('plan')
  const checkout = useCheckoutConfirmation(fromCheckout, pollSession, pollInterval)

  return (
    <div className="flex h-dvh w-dvw items-center justify-center bg-background">
      <div className="w-full max-w-[450px] px-10 text-center">
        <Logo />

        <div className="mt-16">
          {fromCheckout && checkout === 'confirming' ? null : (
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success">
                <Check className="h-8 w-8 text-success-foreground" strokeWidth={3} />
              </div>
            </div>
          )}

          {fromCheckout ? (
            <>
              <h1 className="mb-3 font-semibold text-2xl text-foreground">{CHECKOUT_COPY[checkout].heading}</h1>
              <p className="mb-8 text-[15px] text-muted-foreground">{CHECKOUT_COPY[checkout].body}</p>
            </>
          ) : (
            <>
              <h1 className="mb-6 font-semibold text-2xl text-foreground">You're all set.</h1>

              <div className="mb-8 space-y-0 text-left">
                {infoItems.map((item, index) => (
                  <div
                    className={`flex items-start gap-3 py-3 ${index > 0 ? 'border-border border-t' : ''}`}
                    key={index}
                  >
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success">
                      <Check className="h-2.5 w-2.5 text-success-foreground" strokeWidth={3} />
                    </div>
                    <p className="text-[15px] text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          <Button asChild className="w-full rounded-lg px-8 py-4 font-medium text-[15px]">
            <Link to="/activity">Go to Activity</Link>
          </Button>

          {!fromCheckout && plan ? (
            <p className="mt-4 text-sm">
              <Link className="text-muted-foreground underline" to={`/subscribe?plan=${encodeURIComponent(plan)}`}>
                Choose your plan
              </Link>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
